import { clearWorkspaceSessionKeys } from '@/features/workspaces/workspaceStore';
import { resetNextWorkStore } from '@/features/home/nextWorkStore';
import { resetBridgeStore } from '@/features/needs-you/bridge/bridgeStore';
import { useUserSettingsStore } from '@/features/settings/userSettingsStore';
import { markSessionEnding } from './sessionEnding';

export type LogoutReason =
  | 'idle'
  | 'reconnect_failed'
  | 'session_revoked'
  | 'manual';

// `lvdr_user_settings` is the orphaned localStorage key left behind by the
// now-deleted legacy research-defaults store. It carried account-tied prefs,
// so clear it on logout. (Server-synced settings live in memory now — reset
// below — and are re-fetched from Postgres on the next session's WS load.)
//
// `lvdr_prompt_builder_state` and `knowledge:recentSearches` are the pre-SEC-34
// UNPREFIXED names of two account-tied stores now renamed to `lvdr_session_*`
// (auto-nuked by the prefix sweep below). They stay here as a one-time orphan
// cleanup so data already written under the old names on existing users'
// machines is wiped on the next logout, not left on disk post-lock.
const SESSION_KEYS = [
	'lvdr_auth',
	'lvdr-user-encrypted',
	'lvdr:pinned-research',
	'lvdr_user_settings',
	'lvdr_prompt_builder_state', // legacy (renamed → lvdr_session_prompt_builder_state)
	'knowledge:recentSearches', // legacy (renamed → lvdr_session_knowledge_recent_searches)
];
const SESSION_PREFIX = 'lvdr_session_';

// IndexedDB databases to wipe on logout. INTENTIONALLY EMPTY by default —
// `lvdr-crypto` holds the device keypair (device-tied, not session-tied), and
// wiping it forces full device re-enrollment on next login, which is bad UX for
// a routine lock. `lvdr-wake` is device-tied too and stays, because a signed-out
// wake needs it.
//
// Future contributors: only add a DB name here if it stores session-tied
// data that survives `localStorage.clear()` and must be wiped on logout
// for security reasons. Do NOT add device-identity stores.
const KNOWN_IDB_DATABASES: readonly string[] = [];

/**
 * hardLogout wipes session-tied client state and hard-navigates away.
 *
 * `redirectTo` exists for the one flow that logs out in order to land somewhere
 * OTHER than login: `/demo`, where an already-signed-in visitor chooses to swap
 * onto the shared guest account. Routing that through the normal wipe (rather
 * than a bespoke partial clear in the demo page) is what keeps the previous
 * user's `lvdr_session_*` stores from surviving underneath the demo session —
 * there must be exactly one implementation of "forget this session". Defaults
 * to the login route, so every existing caller is unchanged.
 *
 * Must stay a same-origin absolute path — it is fed straight to
 * `location.replace`, and an attacker-controlled value would be an open
 * redirect. All current callers pass a literal.
 */
export async function hardLogout(
  reason: LogoutReason,
  redirectTo = '/auth/login',
): Promise<void> {
  // Before anything else, and before the first await: from here on nothing may
  // persist session data or prompt on unload (NOTES-15 core-I1). The sweep below
  // and `location.replace` fire pagehide/beforeunload AFTER the wipe, and not
  // every caller flips the auth status first (DeleteAccountSection does not).
  markSessionEnding();

  // Telemetry first — sendBeacon survives page unload.
  try {
    navigator.sendBeacon?.(
      '/api/v2/auth/logout-event',
      JSON.stringify({ reason, at: Date.now() }),
    );
  } catch {
    // ignore
  }

  // Drop every in-memory plaintext Workspace DEK + mounted-session state (E1
  // Phase 13). These live only in JS memory (never localStorage/IDB), so the
  // page unload below would clear them anyway — but do it explicitly so any
  // in-SPA logout path that does NOT unload still fails closed.
  try {
    clearWorkspaceSessionKeys();
  } catch {
    // ignore
  }

  // The bridge release page holds one user's protected Private content (search hits, document and note text,
  // drafts) in memory only. An in-SPA logout does not unload the page, so drop it here (AON-11).
  try {
    resetBridgeStore();
  } catch {
    // ignore
  }

  // Home's next-work list (HOME-02) is one user's task titles, held in memory only.
  try {
    resetNextWorkStore();
  } catch {
    // ignore
  }

  // Wipe session-tied storage; keep machine-only prefs.
  try {
    SESSION_KEYS.forEach((k) => localStorage.removeItem(k));
    Object.keys(localStorage)
      .filter((k) => k.startsWith(SESSION_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }

  // Reset the in-memory server-synced settings bag so the next user on the
  // same tab doesn't transiently see the previous user's research defaults /
  // cost thresholds before the WS settings load completes. The store is not
  // persisted (it mirrors Postgres), so this is a pure in-memory clear; the
  // next session's WS load repopulates it from the server.
  try {
    useUserSettingsStore.setState({ settings: {} });
  } catch {
    // ignore — best-effort; the next WS settings load will overwrite anyway.
  }
  try {
    sessionStorage.clear();
  } catch {
    // ignore
  }
  // IndexedDB: only delete databases explicitly listed in KNOWN_IDB_DATABASES.
  // We do NOT enumerate via indexedDB.databases() — that would catch the
  // device-tied stores (`lvdr-crypto`, `lvdr-wake`), force re-enrollment after
  // every lock and end signed-out wakes. Add to the constant above only when a
  // DB genuinely holds session-tied data.
  if (KNOWN_IDB_DATABASES.length > 0) {
    try {
      await Promise.all(
        KNOWN_IDB_DATABASES.map(
          (n) =>
            new Promise<void>((resolve) => {
              const req = indexedDB.deleteDatabase(n);
              req.onsuccess = () => resolve();
              req.onerror = () => resolve(); // best-effort
              req.onblocked = () => resolve(); // unblock if conn refuses to close
            }),
        ),
      );
    } catch {
      // ignore
    }
  }
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // ignore
  }

  // Hard navigation — wipes JS execution context. The default target is the
  // real login route (`/auth/login`); the app is a static export, so a bare
  // `/login` has no exported page and Angie serves a 404 ("Page not found") —
  // the symptom users hit when an idle/reconnect-failed session tears down on
  // reopening the tab. Keep in sync with routes.ts (`loginWithReturn`).
  // Same-origin paths only (see the doc comment) — never a caller-supplied URL.
  window.location.replace(redirectTo.startsWith('/') ? redirectTo : '/auth/login');
}
